import { createSlice } from '@reduxjs/toolkit';

import { getCandidate as getCandidateAPI, getCandidates as getCandidatesAPI, updateCandidate as updateCandidateAPI } from 'api/candidates';
import { getCandidateAssessmentInfo as getCandidateAssessmentInfoAPI } from 'api/candidate-assessments';

const initialState = {
    list: [],
    detail: null,
    detailAssessments: [],
    candidateOptions: [],
    loading: false,
    detailLoading: false,
    optionsLoading: false,
    updating: false,
    error: '',
    detailError: '',
    total: {
        all: 0,
        items: 0
    },
    assessmentInfo: {
        loading: false,
        data: null
    }
};

const slice = createSlice({
    name: 'candidate',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setDetailLoading(state, action) {
            state.detailLoading = action.payload;
        },
        setOptionsLoading(state, action) {
            state.optionsLoading = action.payload;
        },
        setUpdating(state, action) {
            state.updating = action.payload;
        },
        setError(state, action) {
            state.error = action.payload || '';
        },
        setDetailError(state, action) {
            state.detailError = action.payload || '';
        },
        setCandidates(state, action) {
            state.list = action.payload?.data || [];
            state.total.all = action.payload?.pagination?.total || 0;
            state.total.items = action.payload?.pagination?.totalItems || 0;
        },
        setCandidateOptions(state, action) {
            state.candidateOptions = action.payload || [];
        },
        setCandidateDetail(state, action) {
            state.detail = action.payload?.data || null;
            state.detailAssessments = action.payload?.meta?.assessments || [];
            state.total.all = action.payload?.pagination?.total || 0;
            state.total.items = action.payload?.pagination?.totalItems || 0;
        },
        clearCandidateDetail(state) {
            state.detail = null;
            state.detailAssessments = [];
            state.detailError = '';
        },
        setAssessmentInfoData(state, action) {
            state.assessmentInfo.data = action.payload;
        },
        setAssessmentInfoDataLoading(state, action) {
            state.assessmentInfo.loading = action.payload;
        }
    }
});

export default slice.reducer;

export function fetchCandidates(params = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setLoading(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await getCandidatesAPI(params);
            dispatch(slice.actions.setCandidates(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to load candidates.'));
            throw err;
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchCandidateOptions(params = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setOptionsLoading(true));

        try {
            const response = await getCandidatesAPI(params);
            dispatch(slice.actions.setCandidateOptions(response?.data || []));
            return response?.data || [];
        } finally {
            dispatch(slice.actions.setOptionsLoading(false));
        }
    };
}

export function fetchCandidate(id, params = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setDetailLoading(true));
        dispatch(slice.actions.setDetailError(''));

        try {
            const response = await getCandidateAPI(id, params);
            dispatch(slice.actions.setCandidateDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setDetailError(err?.message || 'Unable to load candidate.'));
            throw err;
        } finally {
            dispatch(slice.actions.setDetailLoading(false));
        }
    };
}

export function updateCandidateInfo(id, data = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setUpdating(true));

        try {
            const response = await updateCandidateAPI(id, data);
            return response;
        } finally {
            dispatch(slice.actions.setUpdating(false));
        }
    };
}

export function fetchCandidateAssessmentInfo(assessmentCode) {
    return async (dispatch) => {
        dispatch(slice.actions.setAssessmentInfoDataLoading(true));
        try {
            const response = await getCandidateAssessmentInfoAPI(assessmentCode);
            dispatch(slice.actions.setAssessmentInfoData(response?.data || null));
            return response;
        } finally {
            dispatch(slice.actions.setAssessmentInfoDataLoading(false));
        }
    };
}

export const { clearCandidateDetail } = slice.actions;
